import React from 'react';
import PropTypes from 'prop-types';
import {
  getContent,
  CONTACT_INFO_PATH,
  standardPhoneSchema,
  standardEmailObjectSchema,
  profileAddressSchema,
  blankSchema,
  getReturnState,
  clearReturnState,
} from 'platform/forms-system/src/js/utilities/data/profile';
import { focusElement } from 'platform/utilities/ui/focus';
import { scrollTo } from 'platform/utilities/scroll';
import {
  EditAddress,
  EditEmail,
  EditHomePhone,
  EditMobilePhone,
} from './EditContactInfo';
import ContactInfo from './ContactInfo';
import ContactInfoReview from './ContactInfoReview';
import { createContactInfoConfirmationField } from './ContactInfoConfirmationField';

/**
 * Add this page containing 4 edit pages to config/form
 * Spread the returned object into the app config/form
 *
 * README: {@link https://va.ghe.com/software/vets-website/tree/main/src/platform/forms-system/src/js/patterns/prefill/README.md|Prefill Usage/Guidance/Examples}
 *
 * @param {Object} [options] - Configuration options for the contact information pages
 * @param {string} [options.addressKey] - address key value set in ContactInfoKeys
 * @param {Object} [options.addressSchema] - Profile address schema object
 * @param {string} [options.contactInfoPageKey] - set page key within the form config chapter
 * @param {string[]} [options.contactInfoRequiredKeys] - array of key values in ContactInfoKeys that are to be required before proceeding
 * @param {Object} [options.contactInfoUiSchema] - custom uiSchema for the contact info page
 * @param {string} [options.contactPath] - Contact info path of formConfig page
 * @param {React.ReactNode} [options.contactInfoPageDescription] - optional content displayed below the page heading
 * @param {React.ReactNode} [options.contactInfoPageTitle] - optional page heading override
 * @param {number} [options.contactSectionHeadingLevel] - heading level for contact info sections
 * @param {import('../utilities/data/profile').ContactInfoContent} [options.content] - Content configuration object
 * @param {Function} [options.depends] - depends callback function; return true to make the main confirmation page visible
 * @param {boolean} [options.disableMockContactInfo] - disable mock contact info data for testing
 * @param {number} [options.editContactInfoHeadingLevel] - heading level for edit contact info pages
 * @param {string} [options.mainHeaderLevel] - heading level for the main page header (e.g. 'h2')
 * @param {boolean} [options.orderAsIncluded=false] - when true, renders contact info cards in the same order as the included array
 * @param {string} [options.emailKey] - email key value set in ContactInfoKeys
 * @param {Object} [options.emailSchema] - Email schema object for email string
 * @param {string} [options.homePhoneKey] - home phone key value set in ContactInfoKeys
 * @param {string[]} [options.included] - array of ContactInfoKeys to show on the contact info page
 * @param {string} [options.mobilePhoneKey] - mobile phone key value set in ContactInfoKeys
 * @param {Object} [options.phoneSchema] - Phone schema object with country code, area code, phone number & extension values
 * @param {boolean} [options.prefillPatternEnabled] - enable prefill pattern for contact info
 * @param {boolean} [options.showMailingAddressUpdateProfileChoice=true] - show the "Do you also want to update this information in your VA.gov profile?" radio options
 *  on the edit mailing address page. When false, the radio is hidden and warning appears.
 * @param {boolean} [options.showProfileAlert=true] - show blue info alert on edit pages ("changes will also be reflected on your profile")
 * @param {boolean} [options.showReviewPageRequiredFields=false] - show Required/Missing card states and submit-attempt errors on Review & Submit
 * @param {boolean} [options.usePageSchemaForRequiredFields=false] - use the page's runtime schema to determine required fields
 * @param {boolean} [options.useWebComponentNavButtons=false] - render the Continue/Back buttons as va-button web components instead of plain buttons
 * @param {string} [options.wrapperKey] - wrapper key value set in ContactInfoKeys
 * @returns {Object} - form config pages for a chapter
 */
const profileContactInfoPages = ({
  content = getContent('application'),
  contactPath = CONTACT_INFO_PATH,
  addressSchema,
  emailSchema,
  phoneSchema,
  // keys to use in form data
  wrapperKey = 'veteran',
  addressKey = 'mailingAddress',
  homePhoneKey = 'homePhone',
  mobilePhoneKey = 'mobilePhone',
  emailKey = 'email',
  // setting these keys enables validation; must use the same keys as above
  contactInfoRequiredKeys = [
    'mailingAddress',
    'email',
    'homePhone',
    'mobilePhone',
  ],
  // Page key used within the formConfig chapter
  contactInfoPageKey = 'confirmContactInfo',
  // Must use same keys as above
  included = ['mobilePhone', 'homePhone', 'mailingAddress', 'email'],
  // depends callback for contact info page
  depends = null,
  contactInfoUiSchema = {},
  contactInfoPageDescription = null,
  contactInfoPageTitle = null,
  disableMockContactInfo = true,
  contactSectionHeadingLevel = null,
  editContactInfoHeadingLevel = null,
  mainHeaderLevel = null,
  orderAsIncluded = false,
  prefillPatternEnabled = true,
  showMailingAddressUpdateProfileChoice = true,
  showProfileAlert = true,
  showReviewPageRequiredFields = false,
  usePageSchemaForRequiredFields = false,
  useWebComponentNavButtons = false,
} = {}) => {
  const config = {};
  const wrapperProperties = {};
  const keys = { wrapper: wrapperKey };
  const requiredList = contactInfoRequiredKeys;

  const getFieldTitle = (formData, fieldKey, editText, addText) => {
    const fieldData = formData?.[wrapperKey]?.[fieldKey];
    if (!fieldData || typeof fieldData !== 'object') return addText;
    const hasData = Object.values(fieldData).some(v => v);
    return hasData ? editText : addText;
  };

  if (included.includes(addressKey)) {
    keys.address = addressKey;
    wrapperProperties[addressKey] =
      addressSchema ||
      (requiredList.includes(addressKey) ? profileAddressSchema : blankSchema);
    config[`${contactInfoPageKey}EditMailingAddress`] = {
      path: `${contactPath}/edit-mailing-address`,
      CustomPage: props =>
        EditAddress({
          ...props,
          title: getFieldTitle(
            props.data,
            addressKey,
            content.editMailingAddress,
            content.addMailingAddress,
          ),
          content,
          contactPath,
          editContactInfoHeadingLevel,
          prefillPatternEnabled,
          showMailingAddressUpdateProfileChoice,
          showProfileAlert,
          requiredKeys: contactInfoRequiredKeys,
          contactInfoPageKey,
          disableMockContactInfo,
          keys,
          formKey: addressKey,
        }),
      CustomPageReview: null, // not shown on review & submit
      depends: () => false, // accessed from contact info page
      uiSchema: {},
      schema: blankSchema,
      onNavBack: ({ goPath }) => goPath(contactPath),
    };
  }

  if (included.includes(homePhoneKey)) {
    keys.homePhone = homePhoneKey;
    wrapperProperties[homePhoneKey] =
      phoneSchema || standardPhoneSchema(requiredList.includes(keys.homePhone));
    config[`${contactInfoPageKey}EditHomePhone`] = {
      path: `${contactPath}/edit-home-phone`,
      CustomPage: props =>
        EditHomePhone({
          ...props,
          title: getFieldTitle(
            props.data,
            homePhoneKey,
            content.editHomePhone,
            content.addHomePhone,
          ),
          content,
          contactPath,
          editContactInfoHeadingLevel,
          showProfileAlert,
          requiredKeys: contactInfoRequiredKeys,
          contactInfoPageKey,
          disableMockContactInfo,
          keys,
        }),
      CustomPageReview: null, // not shown on review & submit
      depends: () => false, // accessed from contact info page
      uiSchema: {},
      schema: blankSchema,
      onNavBack: ({ goPath }) => goPath(contactPath),
    };
  }

  if (included.includes(mobilePhoneKey)) {
    keys.mobilePhone = mobilePhoneKey;
    wrapperProperties[mobilePhoneKey] =
      phoneSchema ||
      standardPhoneSchema(requiredList.includes(keys.mobilePhone));
    config[`${contactInfoPageKey}EditMobilePhone`] = {
      path: `${contactPath}/edit-mobile-phone`,
      CustomPage: props =>
        EditMobilePhone({
          ...props,
          title: getFieldTitle(
            props.data,
            mobilePhoneKey,
            content.editMobilePhone,
            content.addMobilePhone,
          ),
          content,
          contactPath,
          editContactInfoHeadingLevel,
          showProfileAlert,
          requiredKeys: contactInfoRequiredKeys,
          contactInfoPageKey,
          disableMockContactInfo,
          keys,
        }),
      CustomPageReview: null, // not shown on review & submit
      depends: () => false, // accessed from contact info page
      uiSchema: {},
      schema: blankSchema,
      onNavBack: ({ goPath }) => goPath(contactPath),
    };
  }

  if (included.includes(emailKey)) {
    keys.email = emailKey;
    wrapperProperties[emailKey] = emailSchema || standardEmailObjectSchema;
    config[`${contactInfoPageKey}EditEmailAddress`] = {
      path: `${contactPath}/edit-email-address`,
      CustomPage: props =>
        EditEmail({
          ...props,
          title: getFieldTitle(
            props.data,
            emailKey,
            content.editEmail,
            content.addEmail,
          ),
          content,
          contactPath,
          editContactInfoHeadingLevel,
          showProfileAlert,
          requiredKeys: contactInfoRequiredKeys,
          contactInfoPageKey,
          disableMockContactInfo,
          keys,
        }),
      CustomPageReview: null, // not shown on review & submit
      depends: () => false, // accessed from contact info page
      uiSchema: {},
      schema: blankSchema,
      onNavBack: ({ goPath }) => goPath(contactPath),
    };
  }

  // Keep the page schema in one place so the opted-in review page can reuse
  // the same schema without changing the normal contact-information page.
  const contactInfoPageSchema = {
    type: 'object',
    properties: {
      // we're keeping this schema structure where 'veteran' contains all
      // the contact info
      [keys.wrapper]: {
        type: 'object',
        required: contactInfoRequiredKeys,
        properties: wrapperProperties,
      },
    },
  };

  const getReviewSchema = formData => {
    const updateSchema = contactInfoUiSchema?.['ui:options']?.updateSchema;

    // Dynamic Review & Submit requirements are opt-in. Forms that do not
    // enable this option continue using the pattern's existing static schema.
    return showReviewPageRequiredFields && updateSchema
      ? updateSchema(formData, contactInfoPageSchema)
      : contactInfoPageSchema;
  };

  const CustomPage = props => (
    <ContactInfo
      {...props}
      content={content}
      contactPath={contactPath}
      contactInfoPageDescription={contactInfoPageDescription}
      contactInfoPageTitle={contactInfoPageTitle}
      keys={keys}
      requiredKeys={contactInfoRequiredKeys}
      contactInfoPageKey={contactInfoPageKey}
      disableMockContactInfo={disableMockContactInfo}
      contactSectionHeadingLevel={contactSectionHeadingLevel}
      editContactInfoHeadingLevel={editContactInfoHeadingLevel}
      mainHeaderLevel={mainHeaderLevel}
      prefillPatternEnabled={prefillPatternEnabled}
      showReviewPageRequiredFields={showReviewPageRequiredFields}
      usePageSchemaForRequiredFields={usePageSchemaForRequiredFields}
      useWebComponentNavButtons={useWebComponentNavButtons}
      {...(orderAsIncluded ? { order: included } : {})}
    />
  );

  CustomPage.propTypes = {
    contentAfterButtons: PropTypes.node,
    contentBeforeButtons: PropTypes.node,
  };

  // Only opted-in forms receive a review schema; all other consumers keep the
  // same CustomPageReview props they had before this option was introduced.
  const CustomPageReview = props =>
    prefillPatternEnabled ? (
      <CustomPage
        {...props}
        {...(showReviewPageRequiredFields
          ? { schema: getReviewSchema(props.data) }
          : {})}
        onReviewPage
      />
    ) : (
      <ContactInfoReview
        {...props}
        content={content}
        keys={keys}
        contactInfoPageKey={contactInfoPageKey}
      />
    );

  CustomPageReview.propTypes = {
    data: PropTypes.object,
  };

  return {
    [contactInfoPageKey]: {
      title: content.title,
      path: contactPath,
      CustomPage,
      CustomPageReview,
      uiSchema: {
        'ui:confirmationField': createContactInfoConfirmationField({ keys }),
        ...contactInfoUiSchema,
      },
      schema: contactInfoPageSchema,
      depends,
      onFormExit: formData => {
        clearReturnState();
        return formData;
      },
      // overide scroll & focus header
      scrollAndFocusTarget: () => {
        if (!getReturnState()) {
          scrollTo('topContentElement');
          focusElement(`#${contactInfoPageKey}Header`);
        }
      },
    },
    // edit pages; only accessible via ContactInfo component links
    ...config,
  };
};

// review error settings
/**
 * Profile settings
 * @typedef profileReviewErrorOverrideSettings
 * @type {Object}
 * @property {String} contactInfoChapterKey=infoPages - chapter in config/form
 *  containing the contact info pages
 * @property {String} contactInfoPageKey=confirmContactInfo - set page key
 *  within the form config chapter
 * @property {String} wrapperKey=veteran - wrapper key value set in
 *  ContactInfoKeys
 */
export const profileReviewErrorOverride =
  ({
    contactInfoChapterKey = 'infoPages',
    contactInfoPageKey = 'confirmContactInfo',
    wrapperKey = 'veteran',
  } = {}) =>
  err => {
    if (typeof err === 'string' && err.startsWith(wrapperKey)) {
      return {
        chapterKey: contactInfoChapterKey,
        pageKey: contactInfoPageKey,
      };
    }
    return null;
  };

export { profileContactInfoPages };
